import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  FileFilters,
  FileFolder,
  ProjectFile,
  SignedUploadResponse,
} from '../types';

const BASE = '/files';

const toResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results?: T[] }).results ?? [];
  }
  return [];
};

const parentParam = (parentId: string | null): string =>
  parentId === null ? 'root' : parentId;

export const getFolders = async (
  projectId: number,
  parentId: string | null,
  filters: FileFilters = {},
): Promise<FileFolder[]> => {
  const params = new URLSearchParams({
    project_id: String(projectId),
    parent_id: parentParam(parentId),
  });
  if (filters.search) params.set('search', filters.search);

  const response = await GET(`${BASE}/folders/?${params.toString()}`);
  return toResults<FileFolder>(response.data);
};

export const getAllFolders = async (projectId: number): Promise<FileFolder[]> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  const response = await GET(`${BASE}/folders/?${params.toString()}`);
  return toResults<FileFolder>(response.data);
};

export const getFolderById = async (id: string): Promise<FileFolder> => {
  const response = await GET(`${BASE}/folders/${id}/`);
  return response.data as FileFolder;
};

export const createFolder = async (payload: {
  project: number;
  parent: string | null;
  name: string;
}): Promise<FileFolder> => {
  const response = await POST(`${BASE}/folders/`, payload);
  return response.data as FileFolder;
};

export const renameFolder = async (id: string, name: string): Promise<FileFolder> => {
  const response = await PATCH(`${BASE}/folders/${id}/`, { name });
  return response.data as FileFolder;
};

export const moveFolder = async (id: string, parent: string | null): Promise<FileFolder> => {
  const response = await PATCH(`${BASE}/folders/${id}/`, { parent });
  return response.data as FileFolder;
};

export const deleteFolder = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/folders/${id}/`);
};

export const getFiles = async (
  projectId: number,
  folderId: string | null,
  filters: FileFilters = {},
): Promise<ProjectFile[]> => {
  const params = new URLSearchParams({
    project_id: String(projectId),
    folder_id: parentParam(folderId),
  });
  if (filters.search) params.set('search', filters.search);

  const response = await GET(`${BASE}/items/?${params.toString()}`);
  return toResults<ProjectFile>(response.data);
};

export const deleteFile = async (uuid: string): Promise<void> => {
  await DELETE(`${BASE}/items/${uuid}/`);
};

export const moveFile = async (uuid: string, folder: string | null): Promise<ProjectFile> => {
  const response = await PATCH(`${BASE}/items/${uuid}/`, { folder });
  return response.data as ProjectFile;
};

export const requestFileUploadUrl = async (payload: {
  project_id: number;
  folder_id: string | null;
  file_name: string;
  content_type: string;
}): Promise<SignedUploadResponse> => {
  const response = await POST(`${BASE}/items/upload-url/`, payload);
  return response.data as SignedUploadResponse;
};

export const finalizeUploadedFile = async (payload: {
  project_id: number;
  folder_id: string | null;
  file_uuid: string;
  gcs_object_path: string;
  original_file_name: string;
  mime_type?: string;
  file_size?: number;
}): Promise<ProjectFile> => {
  const response = await POST(`${BASE}/items/finalize/`, payload);
  return response.data as ProjectFile;
};

export const uploadFileToSignedUrl = async (
  file: File,
  signedUrl: string,
  onProgress?: (progressPct: number) => void,
): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress((event.loaded / event.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed.')));

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};
